/**
 * StoreFinder Component
 *
 * This component allows users to find a store based on their location.
 * It features a search input for town or postcode, a tabbed navigation
 * to switch between a store list and a map view, and displays a list
 * of stores based on the selected criteria. The component utilizes
 * React hooks for state management and ensures accessibility with
 * appropriate ARIA roles and attributes.
 *
 * Dependencies:
 * - InStoreService: Displays a map view of stores.
 * - StoresList: Lists available stores.
 * - FindStore: Provides functionality to search for stores.
 * - Heroicons: Provides the search icon.
 * - Commercetools UI components: Used for buttons and typography.
 */
import React, { useCallback, useState } from 'react';
import InStoreService from './inStoreService';
import StoresList from './storeList';
import FindStore from './findStore';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import NeighborhoodDiscovery from './NeighborhoodDiscovery';
import { useRouter } from 'next/navigation';
import { FrontasticImage } from 'frontastic/lib/image';
import { Store } from 'shared/types/product/store';

type InStoreServiceCard = {
  title?: string;
  description?: string;
  imageUrl?: FrontasticImage;
  ctaLink?: string;
  ctaName?: string;
};
// Define StoreDataArray as an array of StoreData
export type StoreDataArray = Store[];

// Define the props for the StoreFinder component
export interface StoreFinderProps {
  storeData: StoreDataArray;
  cardData: InStoreServiceCard[];
}

type selectedPlace = {
  name: string;
  location: {
    lat: number;
    lng: number;
  };
};

const StoreFinder = ({ storeData, cardData }: StoreFinderProps) => {
  const [activeTab, setActiveTab] = useState('findStore');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<selectedPlace | undefined>();
  const router = useRouter();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>({} as any);
  const [isOpen, setIsOpen] = useState(false);

  const handleSearch = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (searchQuery) {
      // Implement search logic here
    }
  };
  const handelSelectedPlace = useCallback((selectedPlace: selectedPlace) => {
    router.push(`/find-stores?q=${encodeURIComponent(selectedPlace.name)}`);
    setIsOpen(true);
    setSelectedPlace(selectedPlace);
  }, []);

  return (
    <div className="p-4 md:p-8">
      <div className="my-28 flex justify-center">
        <Typography className="text-29 font-bold uppercase leading-[110%] md:text-36">Find a Store</Typography>
      </div>

      {/* Tab Navigation: Visible only on small to medium screens */}
      <div className="mb-20 mt-32 border-b border-gray-200 md:hidden">
        <nav
          className="-mb-0.5 flex justify-center gap-x-6"
          aria-label="Tabs"
          role="tablist"
          aria-orientation="horizontal"
        >
          <button
            type="button"
            className={`relative inline-flex items-center gap-x-2 whitespace-nowrap border-b-2 border-transparent px-40 py-8 text-16 ${
              activeTab === 'findStore' ? 'font-semibold' : 'font-semibold'
            }`}
            onClick={() => setActiveTab('findStore')}
            role="tab"
            aria-selected={activeTab === 'findStore'}
          >
            List
            {activeTab === 'findStore' && <span className="absolute bottom-0 left-0 right-0 h-4 bg-[#1dce8f]" />}
          </button>
          <button
            type="button"
            className={`relative inline-flex items-center gap-x-2 whitespace-nowrap border-b-2 border-transparent px-40 py-8 text-16 ${
              activeTab === 'inStoreService' ? 'font-semibold' : 'font-semibold'
            }`}
            onClick={() => setActiveTab('inStoreService')}
            role="tab"
            aria-selected={activeTab === 'inStoreService'}
          >
            Map
            {activeTab === 'inStoreService' && <span className="absolute bottom-0 left-0 right-0 h-4 bg-[#1dce8f]" />}
          </button>
        </nav>
      </div>

      {/* Tab Content */}

      <div className="mt-3 flex flex-col md:flex-row">
        {/* Left side: FindStore */}
        <div
          className={`flex w-auto flex-col justify-between pb-20 md:py-20 md:pb-0 ${
            activeTab === 'findStore' ? '' : 'hidden md:block'
          } md:w-[30%]`}
        >
          <FindStore
            storeData={storeData}
            selectedPlace={selectedPlace}
            handleSelectedPlace={handelSelectedPlace}
            userLocation={userLocation}
          />
        </div>

        {/* Right side: NeighborhoodDiscovery */}
        {storeData && (
          <div className={`mt-20 md:flex-1 ${activeTab === 'findStore' ? '' : 'hidden md:block'} md:w-1/2`}>
            <NeighborhoodDiscovery
              selectedPlace={selectedPlace}
              storeData={storeData}
              handleSelectedPlace={handelSelectedPlace}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              setUserLocation={setUserLocation}
            />
          </div>
        )}
      </div>

      <div className={`mt-20 md:mt-30 md:flex-row ${activeTab === 'inStoreService' ? '' : 'hidden md:block'}`}>
        <InStoreService cardData={cardData} />
      </div>

      {/* Always show Stores List */}
      <div>
        <StoresList
          storeData={storeData?.map((store) => ({
            name: store?.name,
            url: store?.name,
          }))}
        />
      </div>
    </div>
  );
};

export default StoreFinder;
